import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button, Alert, Chip, IconButton, Divider, Grid2 as Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxCultureListDto, type DxGrowthResult, type DxSensitivityResult, type DxCultureOrganismInput } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { formatDateTime } from "@/shared/datetime";

const GROWTHS: DxGrowthResult[] = ["NoGrowth", "Growth", "MixedFlora"];
const SIR: DxSensitivityResult[] = ["Sensitive", "Intermediate", "Resistant"];
const sirColor = (r: string) => r === "Resistant" ? "error" : r === "Intermediate" ? "warning" : "success";

export function DiagnosticsMicrobiologyPage() {
  const canManage = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));
  const canEnter = useAuth(s => s.hasPermission("diagnostics.microbiology.enter"));
  const canApprove = useAuth(s => s.hasPermission("diagnostics.microbiology.approve"));
  const qc = useQueryClient();
  const [detailId, setDetailId] = useState<string | null>(null);

  const antibiotics = useQuery({ queryKey: ["dx-antibiotics"], queryFn: () => diagnosticsApi.listAntibiotics() });
  const cultures = useQuery({ queryKey: ["dx-cultures"], queryFn: () => diagnosticsApi.listCultures() });

  // masters
  const [abCode, setAbCode] = useState(""); const [abName, setAbName] = useState(""); const [abClass, setAbClass] = useState("");
  const addAntibiotic = useMutation({
    meta: { successMessage: "Antibiotic added" },
    mutationFn: () => diagnosticsApi.createAntibiotic({ code: abCode.trim(), name: abName.trim(), class: abClass.trim() || null }),
    onSuccess: () => { setAbCode(""); setAbName(""); setAbClass(""); qc.invalidateQueries({ queryKey: ["dx-antibiotics"] }); },
  });

  // culture entry
  const [order, setOrder] = useState(""); const [labTest, setLabTest] = useState(""); const [cultureType, setCultureType] = useState("");
  const [growth, setGrowth] = useState<DxGrowthResult>("Growth");
  const [organisms, setOrganisms] = useState<DxCultureOrganismInput[]>([{ organismName: "", colonyCount: "", sensitivities: [] }]);

  const setOrg = (i: number, patch: Partial<DxCultureOrganismInput>) => setOrganisms(os => os.map((o, j) => j === i ? { ...o, ...patch } : o));
  const addSens = (i: number) => setOrg(i, { sensitivities: [...organisms[i].sensitivities, { antibiotic: "", result: "Sensitive", mic: "" }] });
  const setSens = (i: number, k: number, patch: Partial<{ antibiotic: string; result: DxSensitivityResult; mic: string }>) =>
    setOrg(i, { sensitivities: organisms[i].sensitivities.map((s, j) => j === k ? { ...s, ...patch } : s) });

  const enter = useMutation({
    meta: { successMessage: "Culture result entered" },
    mutationFn: () => diagnosticsApi.enterCulture({
      diagnosticOrderId: order, labTestId: labTest || null, cultureType: cultureType || null, growth,
      organisms: growth === "NoGrowth" ? [] : organisms.filter(o => o.organismName.trim()),
    }),
    onSuccess: () => { setOrder(""); setLabTest(""); setCultureType(""); setOrganisms([{ organismName: "", colonyCount: "", sensitivities: [] }]); qc.invalidateQueries({ queryKey: ["dx-cultures"] }); },
  });

  const columns: GridColDef<DxCultureListDto>[] = [
    { field: "cultureType", headerName: "Culture", width: 150 },
    { field: "growth", headerName: "Growth", width: 120, renderCell: p => <Chip size="small" label={p.row.growth} color={p.row.growth === "NoGrowth" ? "default" : "info"} variant="outlined" /> },
    { field: "organismCount", headerName: "Organisms", width: 100, align: "right", headerAlign: "right" },
    { field: "status", headerName: "Status", width: 120, renderCell: p => <Chip size="small" label={p.row.status} color={p.row.status === "Final" ? "success" : "default"} variant="outlined" /> },
    { field: "createdAt", headerName: "Created", width: 165, valueGetter: v => v ? formatDateTime(v as string) : "" },
    { field: "actions", headerName: "", flex: 1, minWidth: 90, sortable: false, align: "right", renderCell: p => <Button size="small" onClick={() => setDetailId(p.row.id)}>Open</Button> },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · Microbiology</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Antibiotics</Typography>
            {canManage && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField label="Code" size="small" value={abCode} onChange={e => setAbCode(e.target.value)} sx={{ width: 90 }} />
                <TextField label="Name" size="small" value={abName} onChange={e => setAbName(e.target.value)} sx={{ flexGrow: 1 }} />
                <TextField label="Class" size="small" value={abClass} onChange={e => setAbClass(e.target.value)} sx={{ width: 110 }} />
                <IconButton color="primary" disabled={!abCode.trim() || !abName.trim() || addAntibiotic.isPending} onClick={() => addAntibiotic.mutate()}><AddIcon /></IconButton>
              </Stack>
            )}
            {addAntibiotic.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(addAntibiotic.error)}</Alert>}
            <Stack spacing={0.5} sx={{ maxHeight: 320, overflow: "auto" }}>
              {(antibiotics.data ?? []).map(a => <Typography key={a.id} variant="body2">{a.code} — {a.name}{a.class ? ` (${a.class})` : ""}</Typography>)}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Enter culture result</Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <DiagnosticOrderIdField value={order} onChange={setOrder} required sx={{ flexGrow: 1 }} />
                <TextField label="Lab test Id (optional)" size="small" value={labTest} onChange={e => setLabTest(e.target.value)} sx={{ flexGrow: 1 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Culture type" size="small" value={cultureType} onChange={e => setCultureType(e.target.value)} sx={{ flexGrow: 1 }} placeholder="Urine culture" />
                <TextField select label="Growth" size="small" value={growth} onChange={e => setGrowth(e.target.value as DxGrowthResult)} sx={{ width: 150 }}>
                  {GROWTHS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
              </Stack>

              {growth !== "NoGrowth" && organisms.map((o, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField label="Organism" size="small" value={o.organismName} onChange={e => setOrg(i, { organismName: e.target.value })} sx={{ flexGrow: 1 }} />
                    <TextField label="Colony count" size="small" value={o.colonyCount ?? ""} onChange={e => setOrg(i, { colonyCount: e.target.value })} sx={{ width: 160 }} />
                    <IconButton size="small" onClick={() => setOrganisms(os => os.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                  <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
                    {o.sensitivities.map((s, k) => (
                      <Stack key={k} direction="row" spacing={1}>
                        <TextField select label="Antibiotic" size="small" value={s.antibiotic} onChange={e => setSens(i, k, { antibiotic: e.target.value })} sx={{ flexGrow: 1 }}>
                          {(antibiotics.data ?? []).map(a => <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>)}
                        </TextField>
                        <TextField select label="S/I/R" size="small" value={s.result} onChange={e => setSens(i, k, { result: e.target.value as DxSensitivityResult })} sx={{ width: 140 }}>
                          {SIR.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </TextField>
                        <TextField label="MIC" size="small" value={s.mic ?? ""} onChange={e => setSens(i, k, { mic: e.target.value })} sx={{ width: 90 }} />
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addSens(i)} sx={{ alignSelf: "flex-start" }}>Sensitivity</Button>
                  </Stack>
                </Paper>
              ))}
              {growth !== "NoGrowth" && <Button size="small" startIcon={<AddIcon />} onClick={() => setOrganisms(os => [...os, { organismName: "", colonyCount: "", sensitivities: [] }])} sx={{ alignSelf: "flex-start" }}>Organism</Button>}

              {enter.isError && <Alert severity="error">{errorMessage(enter.error)}</Alert>}
              <Button variant="contained" disabled={!order || !canEnter || enter.isPending} onClick={() => enter.mutate()} sx={{ alignSelf: "flex-start" }}>Save culture</Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Cultures</Typography>
            {cultures.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(cultures.error)}</Alert>}
            <Box sx={{ height: 380 }}>
              <DataGrid rows={cultures.data ?? []} columns={columns} loading={cultures.isLoading} getRowId={r => r.id}
                disableRowSelectionOnClick pageSizeOptions={[25]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <CultureDetailDialog id={detailId} canApprove={canApprove} onClose={() => setDetailId(null)} />
    </Box>
  );
}

function CultureDetailDialog({ id, canApprove, onClose }: { id: string | null; canApprove: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [signature, setSignature] = useState("");
  const query = useQuery({ queryKey: ["dx-culture", id], queryFn: () => diagnosticsApi.getCulture(id!), enabled: !!id });
  const r = query.data;
  const finalize = useMutation({
    meta: { successMessage: "Culture finalized" },
    mutationFn: () => diagnosticsApi.finalizeCulture(id!, { signature }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dx-culture", id] }); qc.invalidateQueries({ queryKey: ["dx-cultures"] }); },
  });

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Culture · {r?.cultureType ?? ""}</DialogTitle>
      <DialogContent dividers>
        {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
        {r && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" label={r.status} color={r.status === "Final" ? "success" : "default"} />
              <Chip size="small" label={r.growth} variant="outlined" />
              {r.approvedBy && <Chip size="small" label={`Approved by ${r.approvedBy}`} variant="outlined" />}
            </Stack>
            {r.organisms.map((o, i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">{o.organismName}{o.colonyCount ? ` · ${o.colonyCount}` : ""}</Typography>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Antibiotic</TableCell><TableCell>Result</TableCell><TableCell>MIC</TableCell></TableRow></TableHead>
                  <TableBody>
                    {o.sensitivities.map((s, k) => (
                      <TableRow key={k}><TableCell>{s.antibiotic}</TableCell>
                        <TableCell><Chip size="small" label={s.result} color={sirColor(s.result)} /></TableCell>
                        <TableCell>{s.mic}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
            {r.organisms.length === 0 && <Typography variant="body2" color="text.secondary">No growth.</Typography>}
            {r.status !== "Final" && canApprove && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <TextField label="Digital signature" size="small" fullWidth value={signature} onChange={e => setSignature(e.target.value)} />
                {finalize.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(finalize.error)}</Alert>}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {r?.status !== "Final" && canApprove && <Button variant="contained" disabled={!signature || finalize.isPending} onClick={() => finalize.mutate()}>Finalize</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
